import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { CommonLoader } from '~components/Loaders/CommonLoader';
import useDocumentTitle from '~hooks/useDocumentTitle';
import useOauthAuthorize from '~hooks/useOauth/useOauthAuthorize';
import { extractAxiosErrorInfo } from '~utils/extractAxiosError';
import { clearRememberedSignInRedirect } from '~utils/signInRedirect';

export default function OauthAuthorize() {
  useDocumentTitle();

  const [searchParams] = useSearchParams();
  const { authorize } = useOauthAuthorize();
  const [errorMessage, setErrorMessage] = useState<string>();

  const clientId = searchParams.get('client_id');
  const redirectUri = searchParams.get('redirect_uri');
  const scope = searchParams.get('scope');
  const codeChallenge = searchParams.get('code_challenge');
  const state = searchParams.get('state');
  const nonce = searchParams.get('nonce');

  useEffect(() => {
    const controller = new AbortController();

    async function authorizeAndRedirect() {
      try {
        const callbackUri = await authorize(
          {
            client_id: clientId ?? '',
            redirect_uri: redirectUri ?? '',
            scope: scope ?? '',
            code_challenge: codeChallenge ?? '',
            ...(state ? { state } : {}),
            ...(nonce ? { nonce } : {}),
          },
          controller.signal,
        );
        if (controller.signal.aborted) return;

        window.location.replace(callbackUri);
      } catch (error) {
        if (controller.signal.aborted) return;

        setErrorMessage(
          extractAxiosErrorInfo(error, t('error_messages.oauth.failed_to_authorize'))
            .message,
        );
      } finally {
        clearRememberedSignInRedirect();
      }
    }

    void authorizeAndRedirect();

    return () => controller.abort();
  }, [authorize, clientId, redirectUri, scope, codeChallenge, state, nonce]);

  if (errorMessage) {
    return <Typography sx={{ p: 3 }}>{errorMessage}</Typography>;
  }

  return <CommonLoader text={t('auth.oauth_authorize_redirecting')} />;
}
